import React from 'react';
import type {
  ColumnDataType,
  FilterOperator,
  FilterOption,
} from '../../../types';
import { ColumnDataTypes, FilterOperators } from '../../../types';
import { EnumValuesSection } from './EnumValuesSection';
import { DateValuesSection } from './DateValuesSection';
import { DateRangeValuesSection } from './DateRangeValuesSection';
import { TextValuesSection } from './TextValuesSection';
import { NumberRangeValuesSection } from './NumberRangeValuesSection';

export interface ValuesSectionProps {
  columnDataType: ColumnDataType;
  operator: FilterOperator;
  options: FilterOption[];
  selectedValues: string[];
  validationErrors?: string[];
  onValueChange: (value: string) => void;
  onRangeChange: (startDate: string, endDate: string) => void;
  onValidationError?: (hasError: boolean) => void;
  range?: {
    min: number;
    max: number;
    step?: number;
  };
  emptyOptionLabel?: string;
  defaultRender?: (
    option: FilterOption,
    isSelected: boolean
  ) => React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
}

/**
 * ValuesSection - Dynamic value input component
 * Renders the appropriate input component based on column data type and operator
 */
export const ColumnValues: React.FC<ValuesSectionProps> = ({
  columnDataType,
  operator,
  options,
  selectedValues,
  validationErrors = [],
  onValueChange,
  onRangeChange,
  onValidationError,
  range,
  emptyOptionLabel = 'None',
  defaultRender,
  isLoading = false,
  isError = false,
  onRetry,
  hasMore,
  isFetchingMore,
  onLoadMore,
}) => {
  // Route to the appropriate component based on column data type and operator

  // For DATE type with BETWEEN operator, use date range picker
  if (
    columnDataType === ColumnDataTypes.DATE &&
    operator === FilterOperators.BETWEEN
  ) {
    return (
      <DateRangeValuesSection
        selectedValues={selectedValues}
        onRangeChange={onRangeChange}
        validationErrors={validationErrors}
        onValidationError={onValidationError}
      />
    );
  }

  // For DATE type with other operators, use single date picker
  if (columnDataType === ColumnDataTypes.DATE) {
    return (
      <DateValuesSection
        selectedValues={selectedValues}
        onValueChange={onValueChange}
        validationErrors={validationErrors}
      />
    );
  }

  // For TEXT type, use a free text input
  if (columnDataType === ColumnDataTypes.TEXT) {
    return (
      <TextValuesSection
        selectedValues={selectedValues}
        onValueChange={onValueChange}
        validationErrors={validationErrors}
      />
    );
  }

  // For STRING type with contains operators, use a text input
  if (
    columnDataType === ColumnDataTypes.STRING &&
    (operator === FilterOperators.CONTAINS ||
      operator === FilterOperators.NOT_CONTAINS)
  ) {
    return (
      <TextValuesSection
        selectedValues={selectedValues}
        onValueChange={onValueChange}
        validationErrors={validationErrors}
      />
    );
  }

  // For NUMBER type, use Min/Max numeric inputs
  if (columnDataType === ColumnDataTypes.NUMBER) {
    return (
      <NumberRangeValuesSection
        selectedValues={selectedValues}
        onRangeChange={onRangeChange}
        validationErrors={validationErrors}
        min={range?.min}
        max={range?.max}
      />
    );
  }

  // For all other types, use enum/multi-select section
  return (
    <EnumValuesSection
      options={options}
      selectedValues={selectedValues}
      validationErrors={validationErrors}
      onValueChange={onValueChange}
      emptyOptionLabel={emptyOptionLabel}
      defaultRender={defaultRender}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      hasMore={hasMore}
      isFetchingMore={isFetchingMore}
      onLoadMore={onLoadMore}
    />
  );
};
